import React, { createContext, useContext, useCallback, useRef } from 'react';

export interface AnnotationSaveContextValue {
  /** Acquire the save lock. Waits if another save is in progress (max ~5s). */
  acquireSaveLock: () => Promise<void>;
  /** Release the save lock. Call in finally block. */
  releaseSaveLock: () => void;
  /** Check if a save is currently in progress. */
  isSaveInProgress: () => boolean;
}

const AnnotationSaveContext = createContext<AnnotationSaveContextValue | null>(null);

export function AnnotationSaveProvider({ children }: { children: React.ReactNode }) {
  const lockRef = useRef(false);
  const waitersRef = useRef<Array<() => void>>([]);

  const releaseSaveLock = useCallback(() => {
    const nextWaiter = waitersRef.current.shift();
    if (nextWaiter) {
      // Keep lock held; ownership is handed off to the next waiter.
      nextWaiter();
      return;
    }
    lockRef.current = false;
  }, []);

  const acquireSaveLock = useCallback(async () => {
    if (!lockRef.current) {
      lockRef.current = true;
      return;
    }

    await new Promise<void>((resolve) => {
      waitersRef.current.push(resolve);
    });

    lockRef.current = true;
  }, []);

  const isSaveInProgress = useCallback(() => lockRef.current, []);

  const value: AnnotationSaveContextValue = {
    acquireSaveLock,
    releaseSaveLock,
    isSaveInProgress,
  };

  return (
    <AnnotationSaveContext.Provider value={value}>
      {children}
    </AnnotationSaveContext.Provider>
  );
}

export function useAnnotationSaveContext(): AnnotationSaveContextValue {
  const context = useContext(AnnotationSaveContext);
  if (!context) {
    throw new Error('useAnnotationSaveContext must be used within AnnotationSaveProvider');
  }
  return context;
}

/** Optional hook - returns null if outside provider (for gradual adoption) */
export function useAnnotationSaveContextOptional(): AnnotationSaveContextValue | null {
  return useContext(AnnotationSaveContext);
}
